import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    ApiAction,
    ApiActionDefinition,
    ApiFragment,
    ExtendedApiAction,
    HttpMethod,
    RecordId,
    ShApiCachingHelper
} from '@stylehaven/components';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { MultipleActionResponse, SingleActionResponse } from '../../../entities/actions/action-response.entity';
import { HttpService } from '../../http/http.service';
import trimEnd from 'lodash-es/trimEnd';
import { trimStart } from 'lodash-es';

@Injectable({ providedIn: 'root' })
export class ActionsService {
    protected readonly FALLBACK_VALUE: ApiActionDefinition[] = [];
    protected readonly TYPE_NAME: string = 'ActionDefinition';

    private readonly defaultHeaders: HttpHeaders = new HttpHeaders().set('accept', 'application/hal+json');
    private readonly endpoint: string = 'v2/actions';

    private actionDefinitions$: Observable<ApiActionDefinition[]>;

    constructor(
        private httpClient: HttpClient,
        private httpsService: HttpService,
        private apiCachingHelper: ShApiCachingHelper
    ) {
        this.initialize();
    }

    protected initialize() {
        this.actionDefinitions$ = this.apiCachingHelper.cachedApiCall(this.fetchApiActionDefinitions());
    }

    private fetchApiActionDefinitions(): Observable<ApiActionDefinition[]> {
        return this.httpsService.get(this.endpoint);
    }

    // #region GET

    public getApiActionDefinitions(): Observable<ApiActionDefinition[]> {
        return this.actionDefinitions$.pipe(take(1));
    }

    public enrichApiAction(action: ApiAction): Observable<ExtendedApiAction | null> {
        return this.findApiActionDefinition(action.Rel).pipe(
            map(definition => {
                if (!definition) {
                    console.warn(`No action definition found for action ${action.Rel}`);
                    return null;
                }

                return {
                    ...action,
                    Label: definition.Label
                };
            })
        );
    }

    public findApiActionDefinition(action: string): Observable<ApiActionDefinition | undefined> {
        return this.getApiActionDefinitions().pipe(
            map(apiActions => apiActions.find(apiAction => apiAction.Name === action))
        );
    }
    // #endregion

    //#region execute action
    public executeApiAction(
        action: ApiAction,
        record: ApiFragment,
        body?: any
    ): Observable<null | SingleActionResponse> {
        const url: string = `${action.Href}`;

        switch (action.Method) {
            case HttpMethod.GET:
                return this.httpClient.get<SingleActionResponse>(url, { headers: this.defaultHeaders });
            case HttpMethod.HEAD:
                return this.httpClient.head<SingleActionResponse>(url);
            case HttpMethod.POST:
                return this.httpClient.post<SingleActionResponse>(url, body, { headers: this.defaultHeaders });
            case HttpMethod.PATCH:
                return this.httpClient.patch<SingleActionResponse>(url, body);
            case HttpMethod.PUT:
                return this.httpClient.put<SingleActionResponse>(url, body);
            case HttpMethod.DELETE:
                return this.httpClient.delete<SingleActionResponse>(url, { body });
            default:
                throw new Error(`HttpMethod ${action.Method} currently not supported on the action endpoint.`);
        }
    }

    public bulkExecuteApiAction(
        action: ApiAction,
        recordIds: RecordId[],
        actionArgs?: any
    ): Observable<MultipleActionResponse> {
        const body: any = {
            RecordIds: recordIds,
            Arguments: actionArgs
        };
        const url: string = `${this.endpoint}/${action.Rel}`;

        return this.httpsService.post(url, body);
    }

    // TODO: Remove once portal uses Contact admin action
    public sendEmail(record: ApiFragment, email: string | undefined, baseUrl: string | undefined): void {
        if (!email || !baseUrl) {
            return;
        }
        const fullUrl = `${trimEnd(baseUrl, '/')}/${trimStart(record.getDetailUrl(), '/')}`;
        const content = `${record.getTitle()}:%0D${fullUrl.toString()}%0D`;

        const mailTo = `mailto:${email}`;
        const body = `body=${content}`;
        window.location.href = `${mailTo}?${body}`;
    }
    // #endregion
}
